import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X,
  Sparkles,
  BarChart3,
  User,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  Clock,
  Heart,
  Zap,
  RotateCcw,
  MessageSquare,
  Wand2,
  Target,
  Send,
  Copy,
  Download,
  Edit3,
  Loader2,
  Settings,
  Brain
} from 'lucide-react';
import { Badge } from '../../components/ui';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card/Card';
import { cn } from '../../utils/cn';

export interface AIAnalysisResult {
  sentiment: {
    overall: 'positive' | 'neutral' | 'negative' | 'ERROR';
    score: number;
    confidence: number;
    trend: 'improving' | 'stable' | 'declining' | 'unknown';
  };
  intent: {
    primary: string;
    secondary?: string;
    confidence: number;
  };
  urgency: 'low' | 'medium' | 'high' | 'critical' | 'unknown';
  businessContext: {
    dealValue?: number | string;
    conversionProbability: number;
    nextBestAction: string;
    riskFactors: string[];
    opportunities: string[];
  };
  keyInsights: {
    summary: string;
    keyPoints: string[];
    entities: {
      people: string[];
      companies: string[];
      locations: string[];
      dates: string[];
    };
  };
  recommendations: {
    responseStyle: string;
    suggestedActions: string[];
    warningsFlags: string[];
  };
  performance: {
    responseTime: number;
    analysisDepth: 'basic' | 'comprehensive' | 'deep';
    modelUsed: string;
    confidenceOverall: number;
  };
  error?: string;
}

// Enhanced interface for response generation
export interface AIResponseGeneration {
  id: string;
  content: string;
  subject?: string;
  confidence: number;
  reasoning: string;
  alternatives: string[];
  metadata: {
    model: string;
    temperature: number;
    responseTime: number;
    tokenCount: number;
    conversationContext: number; // Number of messages analyzed
    generation_time?: number;
  };
  purpose?: string;
  tone: string;
  generatedAt: string;
  error?: string;
  message?: string;
  details?: string;
}

interface ConversationPurpose {
  type: 'sales' | 'support' | 'follow_up' | 'nurture' | 'meeting' | 'custom';
  description: string;
  targetOutcome: string;
  instructions?: string;
  tone: 'professional' | 'friendly' | 'casual' | 'formal' | 'empathetic';
  priority: 'low' | 'medium' | 'high';
}

interface AIAnalysisSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  conversationData?: any;
  messages?: any[];
  onAnalysisComplete?: (analysis: AIAnalysisResult) => void;
  onResponseGenerated?: (response: AIResponseGeneration) => void;
}

export const AIAnalysisSidebar: React.FC<AIAnalysisSidebarProps> = ({
  isOpen,
  onClose,
  conversationId,
  conversationData,
  messages = [],
  onAnalysisComplete,
  onResponseGenerated
}) => {
  // Analysis state
  const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // Response generation state
  const [activeTab, setActiveTab] = useState<'analysis' | 'response'>('analysis');
  const [generatedResponse, setGeneratedResponse] = useState<AIResponseGeneration | null>(null);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);
  
  // Purpose and context state
  const [conversationPurpose, setConversationPurpose] = useState<ConversationPurpose>({
    type: 'support',
    description: '',
    targetOutcome: '',
    instructions: '',
    tone: 'professional',
    priority: 'medium'
  });
  
  // Response configuration
  const [responseConfig, setResponseConfig] = useState({
    includeSubject: true,
    generateAlternatives: true,
    temperature: 0.7,
    maxTokens: 500
  });

  // Process conversation through Gemini AI
  const analyzeConversation = useCallback(async () => {
    if (!conversationId || !conversationData) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      console.log('🧠 Starting AI analysis for conversation:', conversationId);
      
      // Get the auth token with the correct key
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/ai/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          includeBusinessContext: true,
          analysisDepth: 'comprehensive',
          includeRecommendations: true,
          includeFullContext: true,
          messages: messages
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        if (errorData.error === 'AI_ANALYSIS_UNAVAILABLE') {
          setAnalysis({
            sentiment: { overall: 'ERROR', score: 0, confidence: 0, trend: 'unknown' },
            intent: { primary: 'AI Unavailable', confidence: 0 },
            urgency: 'unknown',
            businessContext: { 
              dealValue: 'N/A', 
              conversionProbability: 0,
              nextBestAction: 'AI analysis currently unavailable',
              riskFactors: [],
              opportunities: []
            },
            keyInsights: {
              summary: 'AI service unavailable',
              keyPoints: ['Error: AI service unavailable'],
              entities: { people: [], companies: [], locations: [], dates: [] }
            },
            recommendations: {
              responseStyle: 'professional',
              suggestedActions: [],
              warningsFlags: []
            },
            performance: {
              responseTime: 0,
              analysisDepth: 'basic',
              modelUsed: 'unavailable',
              confidenceOverall: 0
            },
            error: 'AI_SERVICE_UNAVAILABLE'
          });
          setError('AI analysis is currently unavailable. Please check system status.');
          return;
        }
        
        throw new Error(`Analysis failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ AI Analysis completed:', result);

      if (result.success && result.data) {
        // Map the API response to our interface
        const analysisResult: AIAnalysisResult = {
          sentiment: {
            overall: result.data.sentiment?.overall || 'neutral',
            score: result.data.sentiment?.score || 0.5,
            confidence: result.data.sentiment?.confidence || 0.5,
            trend: result.data.sentiment?.trend || 'stable'
          },
          intent: {
            primary: result.data.intent?.primary || 'general_inquiry',
            secondary: result.data.intent?.secondary,
            confidence: result.data.intent?.confidence || 0.5
          },
          urgency: result.data.urgency || 'medium',
          businessContext: {
            dealValue: result.data.businessContext?.dealValue,
            conversionProbability: result.data.businessContext?.conversionProbability || 0.5,
            nextBestAction: result.data.nextBestAction || 'Continue conversation and provide helpful information',
            riskFactors: result.data.businessContext?.riskFactors || [],
            opportunities: result.data.businessContext?.opportunities || []
          },
          keyInsights: {
            summary: result.data.keyInsights?.summary || 'Conversation analysis completed',
            keyPoints: result.data.keyInsights?.keyPoints || result.data.keyTopics || [],
            entities: {
              people: result.data.entities?.people || [],
              companies: result.data.entities?.companies || [],
              locations: result.data.entities?.locations || [],
              dates: result.data.entities?.dates || []
            }
          },
          recommendations: {
            responseStyle: result.data.recommendations?.responseStyle || 'professional',
            suggestedActions: result.data.recommendations?.suggestedActions || [],
            warningsFlags: result.data.recommendations?.warningsFlags || []
          },
          performance: {
            responseTime: Date.now() - performance.now(),
            analysisDepth: 'comprehensive',
            modelUsed: result.data.modelUsed || 'gemini-pro',
            confidenceOverall: result.data.confidence || 0.8
          }
        };

        setAnalysis(analysisResult);
        onAnalysisComplete?.(analysisResult);
      } else {
        throw new Error(result.error || 'Failed to process AI analysis');
      }
    } catch (err) {
      console.error('❌ AI Analysis failed:', err);
      setError(err instanceof Error ? err.message : 'Analysis failed');
      
      // Provide fallback analysis
      const fallbackAnalysis: AIAnalysisResult = {
        sentiment: { overall: 'neutral', score: 0.5, confidence: 0.5, trend: 'stable' },
        intent: { primary: 'general_inquiry', confidence: 0.5 },
        urgency: 'medium',
        businessContext: {
          conversionProbability: 0.5,
          nextBestAction: 'Continue conversation professionally',
          riskFactors: [],
          opportunities: []
        },
        keyInsights: {
          summary: 'AI analysis temporarily unavailable. Please try again.',
          keyPoints: [],
          entities: { people: [], companies: [], locations: [], dates: [] }
        },
        recommendations: {
          responseStyle: 'professional',
          suggestedActions: ['Respond promptly', 'Be helpful and courteous'],
          warningsFlags: []
        },
        performance: {
          responseTime: 0,
          analysisDepth: 'basic',
          modelUsed: 'fallback',
          confidenceOverall: 0.3
        }
      };
      
      setAnalysis(fallbackAnalysis);
    } finally {
      setIsAnalyzing(false);
    }
  }, [conversationId, conversationData, messages, onAnalysisComplete]);

  // Generate AI response with full conversation context
  const generateResponse = useCallback(async () => {
    if (!conversationId || !conversationData) return;

    setIsGeneratingResponse(true);
    setResponseError(null);

    try {
      console.log('🤖 Generating AI response for conversation:', conversationId);
      console.log('📋 Purpose:', conversationPurpose);
      console.log('💬 Message context:', messages.length, 'messages');
      
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      
      const response = await fetch(`http://localhost:3001/api/conversations/revolutionary/${conversationId}/ai/generate-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          context: {
            type: conversationPurpose.type,
            description: conversationPurpose.description,
            targetOutcome: conversationPurpose.targetOutcome,
            instructions: conversationPurpose.instructions,
            tone: conversationPurpose.tone,
            priority: conversationPurpose.priority
          },
          conversation_history: messages,
          business_context: analysis?.businessContext,
          preferences: {
            tone: conversationPurpose.tone,
            includeSubject: responseConfig.includeSubject,
            generateAlternatives: responseConfig.generateAlternatives,
            temperature: responseConfig.temperature,
            maxTokens: responseConfig.maxTokens
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Response generation failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ AI Response generated:', result);

      if (result.success && result.data) {
        const aiResponse: AIResponseGeneration = {
          id: `resp_${Date.now()}`,
          content: result.data.content,
          subject: result.data.subject,
          confidence: result.data.confidence || 0.8,
          reasoning: result.data.reasoning || 'Response generated based on conversation context and purpose',
          alternatives: result.data.alternatives || [],
          metadata: {
            model: result.data.metadata?.model_version || 'gemini-pro',
            temperature: responseConfig.temperature,
            responseTime: result.data.metadata?.generation_time || 0,
            tokenCount: result.data.metadata?.token_count || 0,
            conversationContext: messages.length
          },
          purpose: conversationPurpose.description,
          tone: conversationPurpose.tone,
          generatedAt: new Date().toISOString()
        };

        setGeneratedResponse(aiResponse);
        onResponseGenerated?.(aiResponse);
        
        // Auto-switch to response tab to show the result
        setActiveTab('response');
      } else {
        throw new Error(result.error || 'Failed to generate response');
      }
    } catch (err) {
      console.error('❌ Response generation failed:', err);
      setResponseError(err instanceof Error ? err.message : 'Response generation failed');
    } finally {
      setIsGeneratingResponse(false);
    }
  }, [conversationId, conversationData, messages, conversationPurpose, responseConfig, analysis, onResponseGenerated]);

  // Auto-analyze when sidebar opens or conversation changes
  useEffect(() => {
    if (isOpen && conversationId && conversationData) {
      analyzeConversation();
    }
  }, [isOpen, conversationId, conversationData, analyzeConversation]);

  // Auto-refresh analysis every 5 minutes if enabled (was 30 seconds)
  useEffect(() => {
    if (isOpen && autoRefresh && analysis) {
      const interval = setInterval(() => {
        analyzeConversation();
      }, 300000); // 5 minutes instead of 30 seconds

      return () => clearInterval(interval);
    }
  }, [isOpen, autoRefresh, analysis, analyzeConversation]);

  // Copy response to clipboard
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log('✅ Copied to clipboard');
    } catch (err) {
      console.error('❌ Failed to copy to clipboard:', err);
    }
  }, []);

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <Heart className="w-4 h-4 text-green-500" />;
      case 'negative': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <BarChart3 className="w-4 h-4 text-blue-500" />;
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="absolute right-0 top-0 h-full w-96 bg-white dark:bg-neutral-900 shadow-2xl border-l border-neutral-200 dark:border-neutral-800"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                AI Assistant
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`p-1.5 rounded-md transition-colors ${
                  autoRefresh 
                    ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' 
                    : 'text-neutral-400 hover:text-neutral-600'
                }`}
                title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={analyzeConversation}
                disabled={isAnalyzing}
                className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-50"
                title="Refresh analysis"
              >
                <Zap className={`w-4 h-4 ${isAnalyzing ? 'animate-pulse' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Enhanced Tabbed Content */}
          <div className="h-full overflow-hidden">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'analysis' | 'response')} className="h-full flex flex-col">
              <TabsList className="grid grid-cols-2 mx-4 mt-4">
                <TabsTrigger value="analysis" className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Analysis
                </TabsTrigger>
                <TabsTrigger value="response" className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4" />
                  Generate Response
                </TabsTrigger>
              </TabsList>

              {/* Analysis Tab */}
              <TabsContent value="analysis" className="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
            {isAnalyzing && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Analyzing conversation with Gemini AI...
                </p>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
              >
                <div className="flex items-center gap-2 text-red-800 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Analysis Error</span>
                </div>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
              </motion.div>
            )}

            {analysis && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Sentiment Analysis */}
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp className="h-5 w-5 text-blue-500" />
                          <h3 className="font-medium">Sentiment Analysis</h3>
                        </div>
                        
                        {analysis.error ? (
                          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600 dark:text-red-400">
                              AI Analysis Unavailable
                            </span>
                    </div>
                        ) : (
                          <>
                    <div className="flex items-center justify-between mb-2">
                              <span className="text-sm">Overall</span>
                              <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                                analysis.sentiment.overall === 'positive' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                analysis.sentiment.overall === 'negative' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                analysis.sentiment.overall === 'ERROR' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                              }`}>
                                {analysis.sentiment.overall === 'ERROR' ? 'ERROR' : (analysis.sentiment.overall || 'N/A')}
                              </span>
                    </div>
                            
                    <div className="flex items-center justify-between">
                              <span className="text-sm">Confidence</span>
                              <span className="text-sm text-muted-foreground">
                                {analysis.sentiment.confidence || 0}%
                      </span>
                    </div>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {/* Intent Detection */}
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="h-5 w-5 text-purple-500" />
                          <h3 className="font-medium">Intent Detection</h3>
                </div>

                        {analysis.error ? (
                          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600 dark:text-red-400">
                              AI Analysis Unavailable
                            </span>
                    </div>
                        ) : (
                          <>
                      <div className="flex items-center justify-between mb-2">
                              <span className="text-sm">Primary Intent</span>
                              <span className="text-sm font-medium">
                                {analysis.intent.primary === 'AI_UNAVAILABLE' ? 'ERROR' : (analysis.intent.primary || 'N/A')}
                              </span>
                      </div>
                            
                    <div className="flex items-center justify-between">
                              <span className="text-sm">Confidence</span>
                              <span className="text-sm text-muted-foreground">
                                {analysis.intent.confidence || 0}%
                              </span>
                    </div>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {/* Urgency Level */}
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Clock className="h-5 w-5 text-orange-500" />
                          <h3 className="font-medium">Urgency Level</h3>
                </div>

                        {analysis.error ? (
                          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-600 dark:text-red-400">
                              AI Analysis Unavailable
                            </span>
                  </div>
                        ) : (
                          <div className={`text-center py-2 px-3 rounded-lg font-medium ${
                            analysis.urgency === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            analysis.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            analysis.urgency === 'low' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {analysis.urgency === 'unknown' ? 'ERROR' : (analysis.urgency || 'N/A')}
                </div>
                        )}
                      </CardContent>
                    </Card>

                {/* Business Context */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    Business Context
                  </h3>
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-600 dark:text-neutral-400">Conversion Probability</span>
                      <span className="text-sm font-medium">{(analysis.businessContext.conversionProbability * 100).toFixed(0)}%</span>
                    </div>
                    {analysis.businessContext.dealValue && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">Deal Value</span>
                        <span className="text-sm font-medium">${analysis.businessContext.dealValue.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Next Best Action:</p>
                      <p className="text-sm font-medium">{analysis.businessContext.nextBestAction}</p>
                    </div>
                  </div>
                </div>

                {/* Key Insights */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    Key Insights
                  </h3>
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-3">
                    <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
                      {analysis.keyInsights.summary}
                    </p>
                    {analysis.keyInsights.keyPoints.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Key Points:</p>
                        <ul className="space-y-1">
                          {analysis.keyInsights.keyPoints.map((point, index) => (
                            <li key={index} className="text-xs text-neutral-600 dark:text-neutral-400 flex items-start gap-1">
                              <span className="text-primary-500 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary-500" />
                    Recommendations
                  </h3>
                  <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-3 space-y-3">
                    <div>
                      <span className="text-sm text-neutral-600 dark:text-neutral-400">Response Style: </span>
                      <span className="text-sm font-medium">{analysis.recommendations.responseStyle}</span>
                    </div>
                    
                    {analysis.recommendations.suggestedActions.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-2">Suggested Actions:</p>
                        <ul className="space-y-1">
                          {analysis.recommendations.suggestedActions.map((action, index) => (
                            <li key={index} className="text-xs text-neutral-600 dark:text-neutral-400 flex items-start gap-1">
                              <span className="text-green-500 mt-1">✓</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {analysis.recommendations.warningsFlags.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">Warning Flags:</p>
                        <ul className="space-y-1">
                          {analysis.recommendations.warningsFlags.map((warning, index) => (
                            <li key={index} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                              <span className="text-red-500 mt-1">⚠</span>
                              <span>{warning}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Analysis Performance */}
                <div className="text-xs text-neutral-500 dark:text-neutral-400 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <div className="flex justify-between">
                    <span>Model: {analysis.performance.modelUsed}</span>
                    <span>Confidence: {(analysis.performance.confidenceOverall * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Analysis: {analysis.performance.analysisDepth}</span>
                    <span>Updated: {new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
              </motion.div>
            )}
              </TabsContent>

              {/* Response Generation Tab */}
              <TabsContent value="response" className="flex-1 overflow-y-auto p-4 space-y-4 mt-0">
                {/* Purpose/Goal Configuration */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-500" />
                      Conversation Purpose & Goals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Purpose Type */}
                    <div>
                      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Purpose Type</label>
                      <Select 
                        value={conversationPurpose.type} 
                        onValueChange={(value) => setConversationPurpose(prev => ({ ...prev, type: value as any }))}
                      >
                        <SelectTrigger className="h-8 text-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sales">Sales & Conversion</SelectItem>
                          <SelectItem value="support">Customer Support</SelectItem>
                          <SelectItem value="follow_up">Follow-up</SelectItem>
                          <SelectItem value="nurture">Lead Nurturing</SelectItem>
                          <SelectItem value="meeting">Meeting Coordination</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tone */}
                    <div>
                      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Response Tone</label>
                      <Select 
                        value={conversationPurpose.tone} 
                        onValueChange={(value) => setConversationPurpose(prev => ({ ...prev, tone: value as any }))}
                      >
                        <SelectTrigger className="h-8 text-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="casual">Casual</SelectItem>
                          <SelectItem value="formal">Formal</SelectItem>
                          <SelectItem value="empathetic">Empathetic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description/Goal */}
                    <div>
                      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                        Goal Description
                      </label>
                      <Textarea
                        placeholder="What do you want to achieve with this response? e.g., Schedule a demo, resolve support issue, nurture relationship..."
                        value={conversationPurpose.description}
                        onChange={(e) => setConversationPurpose(prev => ({ ...prev, description: e.target.value }))}
                        className="h-16 text-sm mt-1"
                      />
                    </div>

                    {/* Custom Instructions */}
                    <div>
                      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                        Custom Instructions (Optional)
                      </label>
                      <Textarea
                        placeholder="Any specific instructions for the AI? e.g., Include pricing information, mention upcoming features, be cautious about timeline commitments..."
                        value={conversationPurpose.instructions}
                        onChange={(e) => setConversationPurpose(prev => ({ ...prev, instructions: e.target.value }))}
                        className="h-20 text-sm mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Generate Response Button */}
                <Button
                  onClick={generateResponse}
                  disabled={isGeneratingResponse || !conversationPurpose.description.trim()}
                  className="w-full h-10"
                >
                  {isGeneratingResponse ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Response...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate AI Response ({messages.length} messages analyzed)
                    </>
                  )}
                </Button>

                {/* Response Error */}
                {responseError && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                  >
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">Generation Error</span>
                    </div>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{responseError}</p>
                  </motion.div>
                )}

                {/* Generated Response */}
                {generatedResponse && (
                  <div className="space-y-4">
                    {generatedResponse.error ? (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                          <h4 className="font-medium text-red-700 dark:text-red-400">AI Generation Failed</h4>
                        </div>
                        <p className="text-sm text-red-600 dark:text-red-400 mb-2">
                          {generatedResponse.message || 'AI response generation is currently unavailable'}
                        </p>
                        <p className="text-xs text-red-500 dark:text-red-500">
                          Details: {generatedResponse.details || 'Unknown error'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Generated Response Display */}
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-blue-700 dark:text-blue-400">Generated Response</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(generatedResponse.content || '');
                                // Could add a toast notification here
                              }}
                              className="h-8 w-8 p-0"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {generatedResponse.subject && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Subject:</span>
                              <p className="text-sm mt-1">{generatedResponse.subject}</p>
                            </div>
                          )}
                          
                          <div className="mb-3">
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Content:</span>
                            <div className="text-sm mt-1 whitespace-pre-wrap">
                              {generatedResponse.content}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Confidence: {generatedResponse.confidence || 0}%</span>
                            <span>Generated in {generatedResponse.metadata?.generation_time || 0}ms</span>
                          </div>
                        </div>

                        {/* AI Reasoning */}
                        {generatedResponse.reasoning && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Brain className="h-4 w-4 text-purple-500" />
                              <h5 className="text-sm font-medium">AI Reasoning</h5>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {generatedResponse.reasoning}
                            </p>
                          </div>
                        )}

                        {/* Alternative Approaches */}
                        {generatedResponse.alternatives && generatedResponse.alternatives.length > 0 && (
                          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="h-4 w-4 text-green-500" />
                              <h5 className="text-sm font-medium text-green-700 dark:text-green-400">Alternative Approaches</h5>
                            </div>
                            <ul className="space-y-1">
                              {generatedResponse.alternatives.map((alt: string, index: number) => (
                                <li key={index} className="text-xs text-green-600 dark:text-green-400 flex items-start gap-1">
                                  <span className="text-green-400 mt-1">•</span>
                                  <span>{alt}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIAnalysisSidebar; 